import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  CloseIcon,
  Loader
} from '@praticabr/ppsa';
import { useIntl } from 'react-intl';
import ChatFilledIcon from '../../assets/image/chat-filled.svg';
import ExpandIcon from '../../assets/image/expand-icon.png';
import ConversationListIcon from '../../assets/image/conversation-list.svg';
import PanelCollapseIcon from '../../assets/image/panel-collapse.svg';
import PinPanelIcon from '../../assets/image/fixar-painel-chat.svg';
import UnpinPanelIcon from '../../assets/image/desfixar-painel-chat.svg';
import ChatUnreadBadge, {
  formatChatControlLabel
} from '../../components/UIelements/ChatUnreadBadge';
import { useZendeskMessaging } from '../../integrations/ZendeskMessagingProvider';
import { ZendeskUnreadState } from '../../integrations/zendeskMessaging';
import { emitChatMobileNavigationTelemetry } from '../../integrations/chatMobileNavigationTelemetry';
import * as Styled from './styles';
import { ChatDesktopMode, ChatMobileView } from './chatPresentation';
import {
  CHAT_CONVERSATION_LIST_ID,
  CHAT_MESSAGE_LOG_ID,
  CHAT_MOBILE_DIALOG_ID,
  CHAT_MOBILE_TITLE_ID,
  CHAT_PANEL_PIN_UNAVAILABLE_DESCRIPTION_ID
} from './constants';

export {
  CHAT_CONVERSATION_LIST_ID,
  CHAT_MESSAGE_LOG_ID,
  CHAT_MOBILE_DIALOG_ID,
  CHAT_MOBILE_TITLE_ID,
  CHAT_WIDGET_ID
} from './constants';

const DESKTOP_EMBEDDED_TARGETS = {
  conversationList: `#${CHAT_CONVERSATION_LIST_ID}`,
  messageLog: `#${CHAT_MESSAGE_LOG_ID}`
};

type SurfaceStatus = 'loading' | 'ready' | 'identity-conflict' | 'unavailable';

interface PersistentChatSurfaceProps {
  isMobile: boolean;
  desktopMode: ChatDesktopMode;
  mobileOpen: boolean;
  mobileView: ChatMobileView;
  panelPinned: boolean;
  pinAvailable: boolean;
  conversationListCollapsed: boolean;
  unread: ZendeskUnreadState;
  onClosePanel: () => void;
  onMinimizePanel: () => void;
  onMobileConversationSelected: () => void;
  onOpenMobileConversationList: () => void;
  onOpenFullPage: () => void;
  onRequestClose: () => void;
  onRestorePanel: () => void;
  onShowMobileConversation: () => void;
  onTogglePanelPinned: () => void;
  onToggleConversationList: () => void;
}

const setInert = (element: HTMLElement | null, inert: boolean) => {
  if (!element) return;
  if (inert) element.setAttribute('inert', '');
  else element.removeAttribute('inert');
};

const PersistentChatSurface = ({
  isMobile,
  desktopMode,
  mobileOpen,
  mobileView,
  panelPinned,
  pinAvailable,
  conversationListCollapsed,
  unread,
  onClosePanel,
  onMinimizePanel,
  onMobileConversationSelected,
  onOpenMobileConversationList,
  onOpenFullPage,
  onRequestClose,
  onRestorePanel,
  onShowMobileConversation,
  onTogglePanelPinned,
  onToggleConversationList
}: PersistentChatSurfaceProps) => {
  const intl = useIntl();
  const desktopRef = useRef<HTMLElement>(null);
  const splitLayerRef = useRef<HTMLDivElement>(null);
  const conversationListRef = useRef<HTMLElement>(null);
  const messageLogRef = useRef<HTMLElement>(null);
  const overlayRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLSpanElement>(null);
  const mobileListButtonRef = useRef<HTMLSpanElement>(null);
  const panelTitleRef = useRef<HTMLHeadingElement>(null);
  const minimizeButtonRef = useRef<HTMLSpanElement>(null);
  const collapseListControlRef = useRef<HTMLSpanElement>(null);
  const minimizedControlRef = useRef<HTMLDivElement>(null);
  const renderedPresentationRef = useRef<string | null>(null);
  const messengerVisibilityRef = useRef<'visible' | 'hidden' | null>(null);
  const previousDesktopModeRef = useRef<ChatDesktopMode>(desktopMode);
  const previousConversationListCollapsedRef = useRef(conversationListCollapsed);
  const desktopModeRef = useRef<ChatDesktopMode>(desktopMode);
  const mobileOpenRef = useRef(mobileOpen);
  const mobileViewRef = useRef(mobileView);
  const onRequestCloseRef = useRef(onRequestClose);
  const onMobileConversationSelectedRef = useRef(onMobileConversationSelected);
  const [surfaceStatus, setSurfaceStatus] = useState<SurfaceStatus>('loading');
  const [panelView, setPanelView] = useState<'messages' | 'conversations'>('messages');
  const chatVisible = isMobile ? mobileOpen : ['page', 'panel'].includes(desktopMode);
  const viewportPresentation = isMobile ? 'mobile' : 'desktop';
  const [openedPresentation, setOpenedPresentation] = useState<string | null>(
    chatVisible ? viewportPresentation : null
  );
  const messagesEnabled = chatVisible || openedPresentation === viewportPresentation;
  const renderPresentation = `${viewportPresentation}:${messagesEnabled ? 'messages' : 'list'}`;
  useEffect(() => {
    if (chatVisible) setOpenedPresentation(viewportPresentation);
    else if (openedPresentation !== viewportPresentation) setOpenedPresentation(null);
  }, [chatVisible, openedPresentation, viewportPresentation]);
  const {
    status,
    mode,
    renderEmbedded,
    retry,
    subscribeToClose,
    subscribeToConversationOpened,
    deactivateEmbedded,
    reopenEmbedded,
    confirmExistingIdentity,
    continueAnonymously
  } = useZendeskMessaging();
  desktopModeRef.current = desktopMode;
  mobileOpenRef.current = mobileOpen;
  mobileViewRef.current = mobileView;
  onRequestCloseRef.current = onRequestClose;
  onMobileConversationSelectedRef.current = onMobileConversationSelected;
  const splitVisible = ['page', 'panel'].includes(desktopMode);
  const panelTransitionVisible = ['panel', 'minimized'].includes(desktopMode);
  const panelConversationListVisible = desktopMode === 'panel' && panelView === 'conversations';
  const isReady = surfaceStatus === 'ready';
  const mobileConversationListVisible = mobileOpen && mobileView === 'conversationList';
  const mobileMessageLogVisible = mobileOpen && mobileView === 'conversation';
  const panelConversationListLabel = intl.formatMessage({
    id: panelConversationListVisible
      ? 'chat.messageLog'
      : 'chat.expandConversationList'
  });
  const panelConversationListAccessibleLabel = formatChatControlLabel(
    intl,
    unread,
    panelConversationListLabel
  );
  const pinLabel = intl.formatMessage({
    id: panelPinned ? 'chat.unpinPanel' : 'chat.pinPanel'
  });
  const pinUnavailableLabel = intl.formatMessage({
    id: 'chat.pinPanelUnavailable'
  });
  const pinControlDisabled = !panelPinned && !pinAvailable;
  const mobileConversationListLabel = intl.formatMessage({
    id: mobileConversationListVisible
      ? 'chat.returnToConversation'
      : 'chat.expandConversationList'
  });
  const mobileConversationListAccessibleLabel = formatChatControlLabel(
    intl,
    unread,
    mobileConversationListLabel
  );

  useLayoutEffect(() => {
    setInert(desktopRef.current, !['page', 'panel'].includes(desktopMode));
    setInert(overlayRef.current, !mobileOpen);
    setInert(splitLayerRef.current, !splitVisible || !isReady);
    setInert(
      conversationListRef.current,
      !isReady || (isMobile
        ? !mobileConversationListVisible
        : !splitVisible || (desktopMode === 'page'
          ? conversationListCollapsed
          : !panelConversationListVisible))
    );
    setInert(
      messageLogRef.current,
      !isReady || (isMobile
        ? !mobileMessageLogVisible
        : !splitVisible || (desktopMode === 'panel' && panelConversationListVisible))
    );
  }, [
    conversationListCollapsed,
    desktopMode,
    isReady,
    isMobile,
    mobileOpen,
    mobileConversationListVisible,
    mobileMessageLogVisible,
    panelConversationListVisible,
    splitVisible
  ]);

  useEffect(() => {
    if (desktopMode !== 'panel' || panelPinned) return;

    const minimizeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || desktopRef.current?.contains(target)) return;
      onMinimizePanel();
    };

    document.addEventListener('pointerdown', minimizeOnOutsidePointer, true);
    return () => document.removeEventListener('pointerdown', minimizeOnOutsidePointer, true);
  }, [desktopMode, onMinimizePanel, panelPinned]);

  useEffect(() => {
    const previousMode = previousDesktopModeRef.current;
    previousDesktopModeRef.current = desktopMode;
    if (previousMode === 'panel' && desktopMode === 'minimized') {
      minimizedControlRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    } else if (previousMode === 'minimized' && desktopMode === 'panel') {
      minimizeButtonRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    } else if (desktopMode === 'panel') {
      panelTitleRef.current?.focus();
    }
  }, [desktopMode]);

  useLayoutEffect(() => {
    const wasCollapsed = previousConversationListCollapsedRef.current;
    previousConversationListCollapsedRef.current = conversationListCollapsed;
    if (wasCollapsed && !conversationListCollapsed) {
      collapseListControlRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
  }, [conversationListCollapsed]);

  useEffect(() => {
    if (!['authenticated', 'anonymous'].includes(status) || mode !== 'embedded') return;

    return subscribeToClose(() => {
      if (mobileOpenRef.current) onRequestCloseRef.current();
    });
  }, [mode, status, subscribeToClose]);

  useEffect(() => {
    if (!['authenticated', 'anonymous'].includes(status) || mode !== 'embedded') return;

    return subscribeToConversationOpened(() => {
      if (mobileOpenRef.current && mobileViewRef.current === 'conversationList') {
        onMobileConversationSelectedRef.current();
      } else if (desktopModeRef.current === 'panel') {
        setPanelView('messages');
      }
    });
  }, [mode, status, subscribeToConversationOpened]);

  useEffect(() => {
    if (
      !mobileOpen ||
      renderedPresentationRef.current !== 'mobile:messages' ||
      surfaceStatus !== 'ready'
    ) return;
    reopenEmbedded();
  }, [mobileOpen, reopenEmbedded, surfaceStatus]);

  const previousMobileViewRef = useRef(mobileView);
  useLayoutEffect(() => {
    const previousView = previousMobileViewRef.current;
    previousMobileViewRef.current = mobileView;
    if (!mobileOpen || !isReady || previousView === mobileView) return;
    const target = mobileView === 'conversationList'
      ? conversationListRef.current
      : messageLogRef.current;
    target?.focus();
  }, [isReady, mobileOpen, mobileView]);

  useEffect(() => {
    if (isMobile || !messagesEnabled || surfaceStatus !== 'ready') {
      messengerVisibilityRef.current = null;
      return;
    }

    const nextVisibility = ['page', 'panel'].includes(desktopMode)
      ? 'visible'
      : 'hidden';
    const previousVisibility = messengerVisibilityRef.current;
    if (previousVisibility === nextVisibility) return;

    messengerVisibilityRef.current = nextVisibility;
    if (nextVisibility === 'hidden') {
      deactivateEmbedded();
    } else if (previousVisibility === 'hidden') {
      reopenEmbedded();
    }
  }, [deactivateEmbedded, desktopMode, isMobile, messagesEnabled, reopenEmbedded, surfaceStatus]);

  useEffect(() => {
    if (!mobileOpen) return;
    closeButtonRef.current?.querySelector<HTMLButtonElement>('button')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (status === 'unavailable') {
      setSurfaceStatus('unavailable');
      return;
    }

    if (status === 'identity-conflict') {
      setSurfaceStatus('identity-conflict');
      return;
    }

    if (!['authenticated', 'anonymous'].includes(status)) {
      setSurfaceStatus('loading');
      return;
    }

    if (mode !== 'embedded') {
      setSurfaceStatus('unavailable');
      return;
    }

    const presentation = renderPresentation;
    if (renderedPresentationRef.current === presentation) return;
    renderedPresentationRef.current = presentation;
    setSurfaceStatus('loading');

    let active = true;
    // A hidden message log can restore the last conversation and mark it read.
    // Bootstrap only the list until the user explicitly opens the chat.
    void renderEmbedded(
      !messagesEnabled
        ? { conversationList: `#${CHAT_CONVERSATION_LIST_ID}` }
        : DESKTOP_EMBEDDED_TARGETS
    )
      .then(() => {
        if (active) {
          if (messagesEnabled && (isMobile ? !mobileOpenRef.current : !['page', 'panel'].includes(desktopModeRef.current))) {
            deactivateEmbedded();
          }
          setSurfaceStatus('ready');
        }
      })
      .catch(() => {
        if (renderedPresentationRef.current === presentation) {
          renderedPresentationRef.current = null;
        }
        if (active) {
          if (isMobile) {
            emitChatMobileNavigationTelemetry({
              name: 'chat_mobile_presentation_failed',
              stage: 'render'
            });
          }
          setSurfaceStatus('unavailable');
        }
      });

    return () => {
      active = false;
    };
  }, [deactivateEmbedded, isMobile, messagesEnabled, mode, renderEmbedded, renderPresentation, status]);

  const handleRetry = useCallback(async () => {
    renderedPresentationRef.current = null;
    setSurfaceStatus('loading');

    try {
      await retry();
    } catch {
      setSurfaceStatus('unavailable');
    }
  }, [retry]);

  const handleUseExistingIdentity = useCallback(async () => {
    setSurfaceStatus('loading');
    try {
      await confirmExistingIdentity();
    } catch {
      setSurfaceStatus('unavailable');
    }
  }, [confirmExistingIdentity]);

  const handleContinueAnonymously = useCallback(async () => {
    setSurfaceStatus('loading');
    try {
      await continueAnonymously();
    } catch {
      setSurfaceStatus('unavailable');
    }
  }, [continueAnonymously]);

  const focusDialogStart = () => {
    closeButtonRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  };
  const focusDialogEnd = () => {
    if (surfaceStatus === 'ready') {
      const target = mobileView === 'conversationList'
        ? conversationListRef.current
        : messageLogRef.current;
      target?.focus();
    }
    else closeButtonRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  };
  const isBusy = surfaceStatus === 'loading';
  const loadingLabel = intl.formatMessage({ id: 'chat.loading' });
  const minimizedLabel = unread.status === 'known' && unread.count > 0
    ? intl.formatMessage(
      { id: 'chat.restoreWithUnread' },
      { count: unread.count }
    )
    : intl.formatMessage({ id: 'chat.restore' });
  const handleMobileConversationListToggle = () => {
    if (mobileConversationListVisible) onShowMobileConversation();
    else onOpenMobileConversationList();
  };
  const renderStatus = () => surfaceStatus !== 'ready' && (
    <Styled.StatusLayer
      role={surfaceStatus === 'identity-conflict' ? 'alert' : 'status'}
      aria-live='polite'
      aria-label={surfaceStatus === 'loading' ? loadingLabel : undefined}
    >
      {surfaceStatus === 'loading' ? (
        <>
          <Loader />
          <span>{loadingLabel}</span>
        </>
      ) : surfaceStatus === 'identity-conflict' ? (
        <Styled.UnavailableCard>
          <strong>{intl.formatMessage({ id: 'chat.identityConflictTitle' })}</strong>
          <span>{intl.formatMessage({ id: 'chat.identityConflictDescription' })}</span>
          <Styled.StatusActions>
            <Button
              type='button'
              variant='confirmation'
              size='md'
              onClick={() => void handleUseExistingIdentity()}
            >
              {intl.formatMessage({ id: 'chat.useExistingIdentity' })}
            </Button>
            <Button
              type='button'
              variant='outline'
              size='md'
              onClick={() => void handleContinueAnonymously()}
            >
              {intl.formatMessage({ id: 'chat.continueAnonymously' })}
            </Button>
          </Styled.StatusActions>
          <small>{intl.formatMessage({ id: 'chat.anonymousWarning' })}</small>
        </Styled.UnavailableCard>
      ) : (
        <Styled.UnavailableCard>
          <strong>{intl.formatMessage({ id: 'chat.unavailableTitle' })}</strong>
          <span>{intl.formatMessage({ id: 'chat.unavailableDescription' })}</span>
          <Button
            type='button'
            variant='confirmation'
            size='md'
            onClick={() => void handleRetry()}
          >
            {intl.formatMessage({ id: 'chat.retry' })}
          </Button>
        </Styled.UnavailableCard>
      )}
    </Styled.StatusLayer>
  );

  return (
    <Styled.PersistentSurface>
      <Styled.DesktopSurface
        ref={desktopRef}
        $mode={desktopMode}
        $pinned={panelPinned}
        role={desktopMode === 'page'
          ? 'main'
          : desktopMode === 'panel'
            ? 'complementary'
            : 'presentation'}
        aria-label={desktopMode === 'panel'
          ? intl.formatMessage({ id: 'chat.panelLabel' })
          : undefined}
        aria-hidden={!['page', 'panel'].includes(desktopMode)}
        aria-busy={isBusy}
      >
        {panelTransitionVisible && (
          <Styled.PanelHeader>
            <Styled.PanelTitle ref={panelTitleRef} tabIndex={-1}>
              {intl.formatMessage({ id: 'chat.panelTitle' })}
            </Styled.PanelTitle>
            <Styled.PanelActions>
              <Styled.PanelButtonTarget>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  aria-disabled={pinControlDisabled}
                  aria-pressed={panelPinned}
                  aria-describedby={pinControlDisabled
                    ? CHAT_PANEL_PIN_UNAVAILABLE_DESCRIPTION_ID
                    : undefined}
                  aria-label={pinLabel}
                  tooltip={pinControlDisabled ? pinUnavailableLabel : pinLabel}
                  icon={<Styled.HeaderActionIcon
                    $enlargedMask
                    $square
                    $icon={panelPinned ? UnpinPanelIcon : PinPanelIcon}
                    aria-hidden='true'
                  />}
                  onClick={pinControlDisabled ? undefined : onTogglePanelPinned}
                />
              </Styled.PanelButtonTarget>
              <Button
                type='button'
                variant='outline'
                size='sm'
                aria-label={intl.formatMessage({ id: 'chat.openFullPage' })}
                tooltip={intl.formatMessage({ id: 'chat.openFullPage' })}
                icon={<Styled.PanelActionIcon src={ExpandIcon} alt='' aria-hidden='true' />}
                onClick={onOpenFullPage}
              />
              <Styled.PanelButtonTarget>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  aria-pressed={panelConversationListVisible}
                  aria-controls={`${CHAT_CONVERSATION_LIST_ID} ${CHAT_MESSAGE_LOG_ID}`}
                  aria-label={panelConversationListAccessibleLabel}
                  tooltip={panelConversationListLabel}
                  icon={<Styled.HeaderActionIcon $icon={ConversationListIcon} aria-hidden='true' />}
                  onClick={() => setPanelView((current) => current === 'messages'
                    ? 'conversations'
                    : 'messages')}
                />
                <ChatUnreadBadge unread={unread} />
              </Styled.PanelButtonTarget>
              <Styled.PanelButtonTarget ref={minimizeButtonRef}>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  aria-label={intl.formatMessage({ id: 'chat.minimize' })}
                  tooltip={intl.formatMessage({ id: 'chat.minimize' })}
                  icon={<Styled.MinimizeGlyph aria-hidden='true'>−</Styled.MinimizeGlyph>}
                  onClick={onMinimizePanel}
                />
              </Styled.PanelButtonTarget>
              <Button
                type='button'
                variant='outline'
                size='sm'
                aria-label={intl.formatMessage({ id: 'chat.close' })}
                tooltip={intl.formatMessage({ id: 'chat.close' })}
                icon={<CloseIcon color='currentColor' width='30' height='30' />}
                onClick={onClosePanel}
              />
            </Styled.PanelActions>
          </Styled.PanelHeader>
        )}

        {pinControlDisabled && (
          <Styled.VisuallyHidden id={CHAT_PANEL_PIN_UNAVAILABLE_DESCRIPTION_ID}>
            {pinUnavailableLabel}
          </Styled.VisuallyHidden>
        )}

        <Styled.MessengerGrid
          $panel={panelTransitionVisible}
          aria-busy={isBusy}
          data-testid='chat-messenger-grid'
        >
          {!isMobile && (
            <Styled.SplitMessengerGrid
              key={renderPresentation}
              ref={splitLayerRef}
              aria-hidden={!splitVisible || !isReady}
              $collapsed={conversationListCollapsed}
              $panel={desktopMode === 'panel'}
              $visible={splitVisible}
            >
              <Styled.ConversationList
                $active={desktopMode === 'page' || panelConversationListVisible}
                $collapsed={conversationListCollapsed}
                $panel={desktopMode === 'panel'}
              >
                {desktopMode === 'page' && (
                  <Styled.ConversationListHeader>
                    <Styled.ConversationListTitle>
                      {intl.formatMessage({ id: 'chat.conversationList' })}
                    </Styled.ConversationListTitle>
                    <Styled.HeaderIconControl ref={collapseListControlRef} $onPrimary>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        aria-expanded='true'
                        aria-controls={CHAT_CONVERSATION_LIST_ID}
                        aria-label={intl.formatMessage({ id: 'chat.collapseConversationList' })}
                        tooltip={intl.formatMessage({ id: 'chat.collapseConversationList' })}
                        icon={<Styled.HeaderActionIcon $icon={PanelCollapseIcon} aria-hidden='true' />}
                        onClick={onToggleConversationList}
                      />
                    </Styled.HeaderIconControl>
                  </Styled.ConversationListHeader>
                )}
                <Styled.ConversationListTarget
                  ref={conversationListRef}
                  id={CHAT_CONVERSATION_LIST_ID}
                  aria-label={intl.formatMessage({ id: 'chat.conversationList' })}
                  aria-hidden={!isReady || !splitVisible || (desktopMode === 'page'
                    ? conversationListCollapsed
                    : !panelConversationListVisible)}
                />
              </Styled.ConversationList>
              <Styled.MessageLog
                ref={messageLogRef}
                id={CHAT_MESSAGE_LOG_ID}
                aria-label={intl.formatMessage({ id: 'chat.messageLog' })}
                aria-hidden={!isReady || !splitVisible || panelConversationListVisible}
                $active={desktopMode === 'page' || !panelConversationListVisible}
                $panel={desktopMode === 'panel'}
              />
            </Styled.SplitMessengerGrid>
          )}
          {!isMobile && renderStatus()}
        </Styled.MessengerGrid>
      </Styled.DesktopSurface>

      <Styled.MinimizedControlDock
        ref={minimizedControlRef}
        $visible={!isMobile && desktopMode === 'minimized'}
      >
        <Button
          type='button'
          variant='confirmation'
          size='md'
          aria-label={minimizedLabel}
          onClick={onRestorePanel}
        >
          <Styled.MinimizedButtonContent>
            <img src={ChatFilledIcon} alt='' aria-hidden='true' />
            <ChatUnreadBadge unread={unread} />
          </Styled.MinimizedButtonContent>
        </Button>
      </Styled.MinimizedControlDock>

      <Styled.MobileOverlay
        ref={overlayRef}
        id={CHAT_MOBILE_DIALOG_ID}
        role='dialog'
        aria-modal='true'
        aria-labelledby={CHAT_MOBILE_TITLE_ID}
        aria-hidden={!mobileOpen}
        aria-busy={isBusy}
        $open={mobileOpen}
      >
        <Styled.FocusSentinel tabIndex={mobileOpen ? 0 : -1} onFocus={focusDialogEnd} />
        <Styled.MobileHeader>
          <Styled.MobileTitle as='h1' id={CHAT_MOBILE_TITLE_ID} aria-live='polite'>
            {intl.formatMessage({
              id: mobileConversationListVisible
                ? 'chat.conversationList'
                : 'chat.title'
            })}
          </Styled.MobileTitle>
          <Styled.PanelActions>
            <Styled.PanelButtonTarget ref={mobileListButtonRef}>
              <Button
                type='button'
                variant='outline'
                size='sm'
                aria-pressed={mobileConversationListVisible}
                aria-controls={`${CHAT_CONVERSATION_LIST_ID} ${CHAT_MESSAGE_LOG_ID}`}
                aria-label={mobileConversationListAccessibleLabel}
                tooltip={mobileConversationListLabel}
                icon={<Styled.HeaderActionIcon $icon={ConversationListIcon} aria-hidden='true' />}
                onClick={handleMobileConversationListToggle}
              />
              <ChatUnreadBadge unread={unread} />
            </Styled.PanelButtonTarget>
            <Styled.PanelButtonTarget ref={closeButtonRef}>
              <Button
                type='button'
                variant='outline'
                size='sm'
                aria-label={intl.formatMessage({ id: 'chat.close' })}
                tooltip={intl.formatMessage({ id: 'chat.close' })}
                icon={<CloseIcon color='currentColor' width='30' height='30' />}
                onClick={onRequestClose}
              />
            </Styled.PanelButtonTarget>
          </Styled.PanelActions>
        </Styled.MobileHeader>
        <Styled.MobileBody>
          {isMobile && (
            <Styled.MobileTargets
              key={renderPresentation}
              aria-hidden={!isReady}
              $ready={isReady}
            >
              <Styled.MobileEmbeddedTarget
                ref={conversationListRef}
                id={CHAT_CONVERSATION_LIST_ID}
                tabIndex={mobileConversationListVisible && isReady ? 0 : -1}
                aria-label={intl.formatMessage({ id: 'chat.conversationList' })}
                aria-hidden={!isReady || !mobileConversationListVisible}
                $active={mobileConversationListVisible}
              />
              <Styled.MobileEmbeddedTarget
                ref={messageLogRef}
                id={CHAT_MESSAGE_LOG_ID}
                tabIndex={mobileMessageLogVisible && isReady ? 0 : -1}
                aria-label={intl.formatMessage({ id: 'chat.messageLog' })}
                aria-hidden={!isReady || !mobileMessageLogVisible}
                $active={mobileMessageLogVisible}
              />
            </Styled.MobileTargets>
          )}
          {renderStatus()}
        </Styled.MobileBody>
        <Styled.FocusSentinel tabIndex={mobileOpen ? 0 : -1} onFocus={focusDialogStart} />
      </Styled.MobileOverlay>
    </Styled.PersistentSurface>
  );
};

export default PersistentChatSurface;
